import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { LogoIcon } from '../components/Logo';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Crown, Radio, Gamepad2, Target, Swords, MessageCircle, Bell, User,
  LogOut, Wallet, Search, X, Menu, Trophy, Shield, Star, Loader2, LayoutGrid
} from 'lucide-react';
import { BlishAPI } from '@/apis';
import { useStore } from '@/store/useStore';

const NAV_ITEMS = [
  { key: 'dashboard', path: '/dashboard', icon: Crown, label: 'Dashboard', color: 'text-lime' },
  { key: 'stream', path: '/streams', icon: Radio, label: 'Live & Stream', color: 'text-red-400' },
  { key: 'tournaments', path: '/create-challenge', icon: Gamepad2, label: 'Tournaments', color: 'text-muted-gold' },
  { key: 'matchmaking', path: '/matchmaking', icon: Target, label: 'Matchmaking', color: 'text-lime' },
  { key: 'games', path: '/games', icon: LayoutGrid, label: 'Games', color: 'text-lime' },
  { key: 'challenges', path: '/challenges', icon: Swords, label: 'Challenges', color: 'text-muted-gold' },
  { key: 'messages', path: '/messages', icon: MessageCircle, label: 'Messages', color: 'text-gray-300' },
  { key: 'notifications', path: '/notifications', icon: Bell, label: 'Notifications', color: 'text-gray-300' },
  { key: 'profile', path: null, icon: User, label: 'Profile', color: 'text-gray-300' },
];

const MOBILE_TABS = [
  { key: 'dashboard', path: '/dashboard', icon: Crown, label: 'Home' },
  { key: 'stream', path: '/streams', icon: Radio, label: 'Live' },
  { key: 'games', path: '/games', icon: LayoutGrid, label: 'Play' },
  { key: 'challenges', path: '/challenges', icon: Swords, label: 'Battles' },
  { key: 'more', path: null, icon: Menu, label: 'More' },
];

const MORE_MENU_ITEMS = [
  { key: 'wallet', path: '/wallet', icon: Wallet, label: 'Wallet', color: 'text-lime' },
  { key: 'messages', path: '/messages', icon: MessageCircle, label: 'Messages', color: 'text-gray-300' },
  { key: 'notifications', path: '/notifications', icon: Bell, label: 'Notifications', color: 'text-gray-300' },
  { key: 'profile', path: null, icon: User, label: 'Profile', color: 'text-lime' },
  { key: 'matchmaking', path: '/matchmaking', icon: Target, label: 'Matchmaking', color: 'text-lime' },
  { key: 'tournaments', path: '/create-challenge', icon: Gamepad2, label: 'Tournaments', color: 'text-muted-gold' },
  { key: 'leaderboard', path: '/leaderboard', icon: Trophy, label: 'Leaderboard', color: 'text-muted-gold' },
  { key: 'kyc', path: '/kyc/basic', icon: Shield, label: 'KYC Verification', color: 'text-muted-gold' },
];

const getRankColor = (rank) => {
  const r = (rank || '').toLowerCase();
  if (r.includes('legend') || r.includes('crown')) return 'text-lime';
  if (r.includes('elite') || r.includes('diamond')) return 'text-lime';
  if (r.includes('pro') || r.includes('ruby')) return 'text-lime';
  if (r.includes('gold')) return 'text-muted-gold';
  if (r.includes('silver')) return 'text-muted';
  return 'text-muted-gold';
};

const getRankBorder = (rank) => {
  const r = (rank || '').toLowerCase();
  if (r.includes('legend') || r.includes('crown')) return 'ring-[#c8f73a]';
  if (r.includes('elite') || r.includes('diamond')) return 'ring-[#c8f73a]';
  if (r.includes('pro') || r.includes('ruby')) return 'ring-[#c8f73a]';
  return 'ring-gray-600';
};

const SearchBar = ({ user }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await BlishAPI.players.searchPlayer(query)
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const winRate = (w, l) => {
    const total = (w || 0) + (l || 0);
    return total === 0 ? '—' : `${Math.round(((w || 0) / total) * 100)}%`;
  };

  const goToProfile = (playerId) => {
    setOpen(false);
    setQuery('');
    navigate(`/profile/${playerId}`);
  };

  return (
    <div ref={wrapperRef} className="relative max-w-lg mx-auto" data-testid="app-search-wrapper">
      <form onSubmit={e => e.preventDefault()} className="flex items-center bg-white/5 border border-white/10 rounded-full px-4 py-1.5 focus-within:border-[#c8f73a]/50 transition-all">
        <Search className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
        <input
          data-testid="contextual-search-input"
          type="text"
          placeholder="Search players..."
          className="bg-transparent text-sm text-white placeholder-gray-500 outline-none w-full"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
        />
        {query && (
          <button type="button" onClick={() => { setQuery(''); setResults([]); setOpen(false); }} className="text-gray-500 hover:text-white ml-1">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </form>

      {open && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-[#222] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[200] max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-5">
              <Loader2 className="w-5 h-5 animate-spin text-lime" />
            </div>
          )}

          {!loading && results.length === 0 && (
            <div className="text-center py-5 px-4">
              <p className="text-xs text-gray-500">No players found for "{query}"</p>
            </div>
          )}

          {!loading && results.map((player, i) => (
            <div
              key={player.id}
              data-testid={`search-result-${i}`}
              className="p-3 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.04] transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  onClick={() => goToProfile(player.id)}
                  className={`w-9 h-9 rounded-full bg-gray-700/80 ring-2 ${getRankBorder(player.rank)} flex items-center justify-center text-sm font-bold text-white flex-shrink-0 cursor-pointer`}
                >
                  {(player.gamertag || 'U')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => goToProfile(player.id)}>
                  <p className="text-sm font-bold text-white truncate">{player.gamertag}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-semibold ${getRankColor(player.rank)}`}>{player.rank || 'Rookie'}</span>
                    {player.platform && <span className="text-[10px] text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">{player.platform}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2.5 text-[10px] flex-shrink-0">
                  <div className="text-center"><p className="font-bold text-green-400">{player.wins || 0}</p><p className="text-gray-600">W</p></div>
                  <div className="text-center"><p className="font-bold text-red-400">{player.losses || 0}</p><p className="text-gray-600">L</p></div>
                  <div className="text-center"><p className="font-bold text-lime">{winRate(player.wins, player.losses)}</p><p className="text-gray-600">WR</p></div>
                  <div className="text-center"><p className="font-bold text-muted-gold">{player.backer_count || 0}</p><p className="text-gray-600">BC</p></div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => goToProfile(player.id)} data-testid={`search-view-${i}`}
                  className="flex-1 flex items-center justify-center gap-1 h-7 bg-white/5 border border-white/10 rounded-lg text-[11px] font-semibold text-gray-300 hover:bg-white/10 transition-all active:scale-95">
                  <User className="w-3 h-3" />Profile
                </button>
                <button onClick={() => { setOpen(false); setQuery(''); navigate(`/create-challenge?opponent=${player.id}`); }} data-testid={`search-challenge-${i}`}
                  className="flex-1 flex items-center justify-center gap-1 h-7 bg-lime/10 border border-lime/20 rounded-lg text-[11px] font-semibold text-lime hover:bg-lime/20 transition-all active:scale-95">
                  <Swords className="w-3 h-3" />Challenge
                </button>
                <button onClick={() => { setOpen(false); setQuery(''); navigate(`/messages?to=${player.id}`); }} data-testid={`search-message-${i}`}
                  className="flex-1 flex items-center justify-center gap-1 h-7 bg-white/5 border border-white/10 rounded-lg text-[11px] font-semibold text-gray-300 hover:bg-white/10 transition-all active:scale-95">
                  <MessageCircle className="w-3 h-3" />Message
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AppLayout() {
  const updateWallet = useStore((state) => state.updateWallet);
  const user = useStore((state) => state.user);
  const logout = useStore((state) => state.logout);

  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [walletBalance, setWalletBalance] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await BlishAPI.notifications.getNotifications();
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch {}
  }, [user]);

  const fetchWallet = useCallback(async () => {
    if (!user) return;
   
    try {
      const {data} = await BlishAPI.wallet.getWallet();
      updateWallet({
        blish_crowns: data.blish_crowns,
        locked_crowns: data.locked_crowns
      })
      
      setWalletBalance(data.blish_crowns);
    } catch (e) {
      // Silent: wallet fetch failures are expected when unauthenticated or offline
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    fetchWallet();
    const interval = setInterval(() => { fetchNotifications(); fetchWallet(); }, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchWallet]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen w-full flex flex-col bg-[#1a1a1a] text-[#F0F0F0] overflow-hidden">
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT SIDEBAR — desktop only */}
        <aside
          data-testid="app-sidebar"
          onMouseEnter={() => setExpanded(true)}
          onMouseLeave={() => setExpanded(false)}
          className={`hidden md:flex flex-shrink-0 h-full bg-[#1a1a1a] border-r border-[#333] flex-col transition-all duration-300 z-50 ${expanded ? 'w-52' : 'w-16'}`}
        >
          <div className="flex items-center justify-center h-14 border-b border-white/5">
            <Link to="/dashboard" data-testid="sidebar-logo"><LogoIcon size="sm" /></Link>
          </div>
          <nav aria-label="App sidebar" className="flex-1 py-3 space-y-1 px-2 overflow-y-auto hide-scrollbar">
            {NAV_ITEMS.filter(item => !user ? (item.key === 'dashboard' || item.key === 'stream' || item.key === 'games' || item.key === 'tournaments' || item.key === 'matchmaking') : true).map((item) => {
              const path = item.key === 'profile' ? `/profile/${user?.id}` : item.path;
              const active = isActive(path);
              return (
                <Link key={item.key} to={path} data-testid={`sidebar-${item.key}`} aria-current={active ? 'page' : undefined}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group relative ${active ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}>
                  <item.icon className={`w-5 h-5 flex-shrink-0 ${active ? item.color : ''}`} />
                  <span className={`text-sm font-semibold whitespace-nowrap transition-opacity duration-200 ${expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>{item.label}</span>
                  {item.key === 'notifications' && unreadCount > 0 && (
                    <span className={`absolute ${expanded ? 'right-2' : 'top-1 right-1'} bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1`}>{unreadCount}</span>
                  )}
                  {item.key === 'messages' && unreadMessages > 0 && (
                    <span className={`absolute ${expanded ? 'right-2' : 'top-1 right-1'} bg-green-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1`}>{unreadMessages}</span>
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="border-t border-white/5 py-3 px-2 space-y-1">
            {user ? (
              <>
            <Link to="/wallet" data-testid="sidebar-wallet"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-500 hover:text-lime hover:bg-white/5 transition-all">
              <Wallet className="w-5 h-5 flex-shrink-0" />
              <span className={`text-sm font-bold text-lime whitespace-nowrap transition-opacity duration-200 ${expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
                {walletBalance !== null ? `${Math.floor(walletBalance)} BC` : '...'}
              </span>
            </Link>
            <button onClick={handleLogout} data-testid="sidebar-logout"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all w-full">
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className={`text-sm font-semibold whitespace-nowrap transition-opacity duration-200 ${expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>Logout</span>
            </button>
              </>
            ) : (
              <Link to="/auth" data-testid="sidebar-signin"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-500 hover:text-lime hover:bg-white/5 transition-all">
                <User className="w-5 h-5 flex-shrink-0" />
                <span className={`text-sm font-bold text-lime whitespace-nowrap transition-opacity duration-200 ${expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>Sign In</span>
              </Link>
            )}
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col overflow-hidden" data-testid="app-main-content">
          {/* Search Bar with inline dropdown */}
          <div className="flex-shrink-0 border-b border-[#333] px-3 py-2 bg-[#1a1a1a]/95">
            <SearchBar user={user} />
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto hide-scrollbar pb-24 md:pb-0">
            <Outlet />
          </div>
        </main>
      </div>

      {/* MORE MENU OVERLAY (mobile) */}
      <AnimatePresence>
        {showMoreMenu && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm" onClick={() => setShowMoreMenu(false)}>
            <motion.div initial={{ y: 300 }} animate={{ y: 0 }} exit={{ y: 300 }}
              className="absolute bottom-0 left-0 right-0 bg-[#222] border-t border-[#333] rounded-t-2xl p-4 pb-8"
              onClick={e => e.stopPropagation()}>
              <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4" />
              <div className="space-y-1">
                {user ? (
                  <>
                <Link to="/wallet" onClick={() => setShowMoreMenu(false)} data-testid="more-menu-wallet"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 transition-all">
                  <Wallet className="w-5 h-5 text-lime" />
                  <span className="text-sm font-semibold text-white flex-1">Wallet</span>
                  <span className="text-sm font-bold text-lime font-mono">{walletBalance !== null ? `${Math.floor(walletBalance)} BC` : '...'}</span>
                </Link>
                {MORE_MENU_ITEMS.filter(i => i.key !== 'wallet').map(item => {
                  const path = item.key === 'profile' ? `/profile/${user?.id}` : item.path;
                  return (
                    <Link key={item.key} to={path} onClick={() => setShowMoreMenu(false)} data-testid={`more-menu-${item.key}`}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 transition-all">
                      <item.icon className={`w-5 h-5 ${item.color}`} />
                      <span className="text-sm font-semibold text-white">{item.label}</span>
                      {item.key === 'notifications' && unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{unreadCount}</span>
                      )}
                      {item.key === 'messages' && unreadMessages > 0 && (
                        <span className="bg-green-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{unreadMessages}</span>
                      )}
                    </Link>
                  );
                })}
                <button onClick={() => { setShowMoreMenu(false); handleLogout(); }} data-testid="more-menu-logout"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-500/10 transition-all w-full">
                  <LogOut className="w-5 h-5 text-red-400" />
                  <span className="text-sm font-semibold text-red-400">Logout</span>
                </button>
                  </>
                ) : (
                  <>
                <Link to="/tournaments" onClick={() => setShowMoreMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 transition-all">
                  <Swords className="w-5 h-5 text-muted-gold" />
                  <span className="text-sm font-semibold text-white">Tournaments</span>
                </Link>
                <Link to="/auth" onClick={() => setShowMoreMenu(false)} data-testid="more-menu-signin"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/5 transition-all">
                  <User className="w-5 h-5 text-lime" />
                  <span className="text-sm font-semibold text-lime">Sign In</span>
                </Link>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE BOTTOM TAB BAR — fixed floating */}
      <nav aria-label="Main navigation" data-testid="mobile-bottom-tabs" className="md:hidden fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom,0px)+12px)] z-50 bg-[#1a1a1a]/95 backdrop-blur-md border border-[#333] rounded-2xl shadow-[0_-6px_30px_rgba(0,0,0,0.6)] flex items-center justify-around px-2 py-1.5">
        {MOBILE_TABS.map((tab) => {
          if (tab.key === 'more') {
            return (
              <button key={tab.key} onClick={() => setShowMoreMenu(true)} data-testid="mobile-tab-more" aria-haspopup="menu" aria-expanded={showMoreMenu}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[52px] py-1.5 rounded-lg transition-all ${showMoreMenu ? 'text-white' : 'text-gray-600'}`}>
                <Menu className={`w-5 h-5 ${showMoreMenu ? 'text-lime' : ''}`} />
                <span className="text-[10px] font-semibold leading-tight">More</span>
              </button>
            );
          }
          if (tab.key === 'challenges' && !user) {
            return (
              <Link key="signin" to="/auth" data-testid="mobile-tab-signin"
                className="flex flex-col items-center justify-center gap-0.5 min-w-[52px] py-1.5 rounded-lg transition-all text-gray-600 hover:text-white">
                <User className="w-5 h-5 text-lime" />
                <span className="text-[10px] font-semibold leading-tight">Sign In</span>
              </Link>
            );
          }
          const path = tab.key === 'profile' ? `/profile/${user?.id}` : tab.path;
          const active = isActive(path);
          return (
            <Link key={tab.key} to={path} data-testid={`mobile-tab-${tab.key}`} aria-current={active ? 'page' : undefined}
              className={`flex flex-col items-center justify-center gap-0.5 min-w-[52px] py-1.5 rounded-lg transition-all ${active ? 'text-white' : 'text-gray-600'}`}>
              <tab.icon className={`w-5 h-5 ${active ? 'text-lime' : ''}`} />
              <span className="text-[10px] font-semibold leading-tight">{tab.label}</span>
              {tab.key === 'notifications' && unreadCount > 0 && (
                <span className="absolute -top-0.5 right-0 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5">{unreadCount}</span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
